/**
 * Result normalisation.
 *
 * Two surfaces, one binding runtime, and a fixture loader all hand results to
 * this package, and they will not agree on an envelope shape. Rather than force
 * a nominal type across three packages owned by three different agents, this
 * accepts anything result-shaped and reduces it. Unknown input yields an empty
 * result, never a throw — a renderer that crashes on a shape surprise is worse
 * than one that renders an honest empty state.
 */
import type { CapabilityResult, Freshness, FreshnessState } from './types.js';
import { asRows } from './slots.js';

function isRecord(v: unknown): v is Record<string, unknown> {
  return v != null && typeof v === 'object' && !Array.isArray(v);
}

const ROW_KEYS = ['rows', 'items', 'data', 'results', 'records'] as const;
const APPLIED_KEYS = ['applied', 'input', 'args', 'arguments', 'params', 'filters', 'query'] as const;

export function normalizeFreshness(input: unknown, now: Date = new Date()): Freshness {
  if (!isRecord(input)) return { state: 'unknown' };

  /**
   * `asOf` is the spelling in `WebMcpToolResult` — the canonical envelope in
   * @clawharbor/manifest that apps/server actually emits. Reading only
   * `fetchedAt` left every server-served result with no timestamp, so the
   * freshness chip silently degraded to a bare state label with no age.
   */
  const fetchedAt =
    typeof input['fetchedAt'] === 'string'
      ? input['fetchedAt']
      : typeof input['asOf'] === 'string'
        ? input['asOf']
        : undefined;
  const ttlSec = typeof input['ttlSec'] === 'number' ? input['ttlSec'] : undefined;
  const origin = typeof input['origin'] === 'string' ? input['origin'] : undefined;
  const note = typeof input['note'] === 'string' ? input['note'] : undefined;

  let ageSec = typeof input['ageSec'] === 'number' ? input['ageSec'] : undefined;
  if (ageSec == null && fetchedAt) {
    const t = Date.parse(fetchedAt);
    if (Number.isFinite(t)) ageSec = Math.max(0, Math.round((now.getTime() - t) / 1000));
  }

  const declared = input['state'];
  let state: FreshnessState;
  if (typeof declared === 'string' && ['live', 'cached', 'stale', 'fixture', 'unknown'].includes(declared)) {
    state = declared as FreshnessState;
  } else if (input['fixture'] === true) {
    state = 'fixture';
  } else if (input['stale'] === true) {
    state = 'stale';
  } else if (input['cached'] === true) {
    // A cache hit past its TTL is stale even when nobody labelled it so.
    state = ttlSec != null && ageSec != null && ageSec > ttlSec ? 'stale' : 'cached';
  } else if (fetchedAt != null) {
    state = ttlSec != null && ageSec != null && ageSec > ttlSec ? 'stale' : ageSec != null && ageSec < 60 ? 'live' : 'cached';
  } else {
    state = 'unknown';
  }

  return { state, fetchedAt, ttlSec, ageSec, origin, note };
}

export function normalizeResult(input: unknown, now: Date = new Date()): CapabilityResult {
  const empty: CapabilityResult = { rows: [], freshness: { state: 'unknown' }, applied: {}, notes: [] };

  if (input == null) return empty;
  if (Array.isArray(input)) return { ...empty, rows: asRows(input) };
  if (!isRecord(input)) return empty;

  let rows: unknown[] = [];
  for (const key of ROW_KEYS) {
    const v = input[key];
    if (Array.isArray(v)) {
      rows = v;
      break;
    }
  }

  let applied: Record<string, unknown> = {};
  for (const key of APPLIED_KEYS) {
    const v = input[key];
    if (isRecord(v)) {
      applied = v;
      break;
    }
  }

  const meta = isRecord(input['meta']) ? input['meta'] : undefined;
  const freshness = normalizeFreshness(input['freshness'] ?? meta?.['freshness'] ?? meta ?? input, now);

  /**
   * Two spellings, because the two producers disagree. The binding runtime
   * returns `notes: string[]`; `WebMcpToolResult` flattens them into a single
   * `note` string joined with "; ", and that is what crosses the wire from
   * apps/server. Reading only the array dropped every server-side note —
   * including "truncated to 500 of N rows" and the deep-link removals, which
   * are precisely the ones a reader must see.
   */
  const notesRaw = input['notes'];
  const notes = Array.isArray(notesRaw)
    ? notesRaw.filter((n): n is string => typeof n === 'string')
    : typeof input['note'] === 'string' && input['note'] !== ''
      ? input['note'].split('; ').filter(Boolean)
      : [];

  const totalRaw = input['total'] ?? input['count'] ?? meta?.['total'];
  const total = typeof totalRaw === 'number' && totalRaw >= rows.length ? totalRaw : undefined;

  return { rows: asRows(rows), freshness, applied, notes, total };
}
